import { useEffect } from 'react';
import { Button, Icon, Text } from '@shopify/polaris';
import { ChatIcon, InfoIcon, XIcon } from '@shopify/polaris-icons';
import { handleOpenChat } from '@/helpers/crisp';
import { GUIDES, MCP_URL_DISPLAY, ProviderBrand } from '../../config';
import UrlField from './UrlField';
import {
  StyledBackdrop,
  StyledBody,
  StyledFooter,
  StyledHeader,
  StyledModal,
  StyledNotice,
  StyledOfficialGuide,
  StyledSteps,
} from './styled';

export type ProviderKey = ProviderBrand;

interface IHowToConnectModalProps {
  provider: ProviderKey | null;
  onClose: () => void;
}

const HowToConnectModal = ({ provider, onClose }: IHowToConnectModalProps) => {
  useEffect(() => {
    if (!provider) return undefined;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [provider, onClose]);

  if (!provider) return null;
  const guide = GUIDES[provider];

  return (
    <StyledBackdrop data-testid="modal-backdrop" onClick={onClose}>
      <StyledModal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <StyledHeader>
          <Text as="h2" variant="headingMd">
            {guide.title}
          </Text>
          <button type="button" className="close" onClick={onClose} aria-label="Close">
            <Icon source={XIcon} />
          </button>
        </StyledHeader>
        <StyledBody>
          <Text as="p">{guide.intro}</Text>
          <UrlField url={MCP_URL_DISPLAY} />
          <Text as="p" fontWeight="semibold">
            Setup steps:
          </Text>
          <StyledSteps>
            {guide.steps.map((step, idx) => (
              <li key={`${provider}-step-${idx}`}>{step}</li>
            ))}
          </StyledSteps>
          <StyledNotice>
            <Icon source={InfoIcon} tone="info" />
            <span>
              Setup steps in the AI client may change over time. For the most accurate instructions, follow the official guide below.
            </span>
          </StyledNotice>
          <StyledOfficialGuide>
            <Text as="p">
              📖 Official guide:{' '}
              <a href={guide.officialGuide.url} target="_blank" rel="noopener noreferrer">
                {guide.officialGuide.label}
              </a>
            </Text>
          </StyledOfficialGuide>
        </StyledBody>
        <StyledFooter $hasSupport={!!guide.showSupport}>
          {guide.showSupport && (
            <Button icon={ChatIcon} onClick={handleOpenChat}>
              Contact support
            </Button>
          )}
          <Button variant={guide.showSupport ? undefined : 'primary'} onClick={onClose}>
            Got it
          </Button>
        </StyledFooter>
      </StyledModal>
    </StyledBackdrop>
  );
};

export default HowToConnectModal;
